/**
 * DogAvatar Component
 *
 * Displays a dog's avatar with fallback to initial when image fails to load.
 * Handles image loading errors gracefully.
 */

import { useState, useCallback, useEffect } from 'react';
import { cn } from '@/shared/lib/utils';

interface DogAvatarProps {
  src: string | null | undefined;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  borderColor?: string;
  fallbackBgColor?: string;
  fallbackTextColor?: string;
}

const sizeClasses = {
  sm: 'w-10 h-10 text-sm',
  md: 'w-12 h-12 text-lg',
  lg: 'w-14 h-14 text-xl',
  xl: 'w-24 h-24 text-3xl',
};

export function DogAvatar({
  src,
  name,
  size = 'md',
  className,
  borderColor,
  fallbackBgColor = '#E0F2FE', // primary-100
  fallbackTextColor = '#0369A1', // primary-700
}: DogAvatarProps) {
  const [hasError, setHasError] = useState(false);

  // Reset error when src changes
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  const showImage = src && !hasError;

  const baseStyles: React.CSSProperties = {};
  if (borderColor) {
    baseStyles.borderColor = borderColor;
  }

  if (showImage) {
    return (
      <img
        src={src}
        alt={name}
        className={cn(
          'rounded-full object-cover',
          sizeClasses[size],
          className
        )}
        style={baseStyles}
        onError={handleError}
      />
    );
  }

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-bold',
        sizeClasses[size],
        className
      )}
      style={{
        ...baseStyles,
        backgroundColor: fallbackBgColor,
        color: fallbackTextColor,
      }}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
}
